import React, { useMemo, useState } from 'react';
import {
  Box, Text, VStack, HStack, Heading, Card, Pressable, Button, ButtonIcon,
} from '@gluestack-ui/themed';
import { X } from 'lucide-react-native';
import { Modal } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { Task } from '@/common/api/models/task';
import { useEvaluationGradingConfig } from '@/lecturer/EvaluationDesk/context/EvaluationGradingConfigContext';
import { useTenantEventKinds } from '@/common/context/TenantSettingsStore';
import type { PerformanceLevelConfig } from '@/common/api/models/performanceLevelConfig';
import type { EvalDimRating } from '@/common/api/models/evalDimRating';
import { taskRequiresEvaluation } from '@/common/utils/taskEvaluationUtils';
import { getFlatEvaluationDimensions } from '@/common/utils/evaluation/getFlatEvaluationDimensions';
import { formatPerformanceLevelDisplay } from '@/lecturer/EvaluationDesk/utils/gradingConversions';

interface EssayDimensionInsightsProps {
  tasks: Task[];
}

interface DimensionStats {
  total: number;
  counts: Record<string, number>;
  predominantLevel: string;
  applicableCount: number;
  comments: string[];
  score: number; // Weighted average score between 0 and 1
  transferTaskCount: number;
  regularTaskCount: number;
}

interface DimensionDetailModalProps {
  dimension: string;
  stats: DimensionStats;
  isVisible: boolean;
  onClose: () => void;
  performanceLevels: PerformanceLevelConfig[];
  dimensionColor: string;
}

const DimensionDetailModal: React.FC<DimensionDetailModalProps> = ({
  dimension,
  stats,
  isVisible,
  onClose,
  performanceLevels,
  dimensionColor,
}) => {
  const { t } = useTranslation();

  return (
    <Modal visible={isVisible} transparent>
      <Box flex={1} bg="rgba(0, 0, 0, 0.5)" justifyContent="center" alignItems="center">
        <Box bg="white" w="$3/4" maxWidth={600} p="$4" borderRadius="$md">
          <HStack justifyContent="space-between" alignItems="center" mb="$4">
            <Heading size="md" color={dimensionColor}>
              {t(`common.objective-dimensions.${dimension}`)}
            </Heading>
            <Button variant="link" onPress={onClose} p="$0">
              <ButtonIcon as={X} />
            </Button>
          </HStack>

          <VStack space="sm" mb="$4">
            {performanceLevels.map((level) => {
              const count = stats.counts[level.id] || 0;
              const percentage = Math.round((count / stats.total) * 100);
              if (count === 0) return null;

              return (
                <VStack key={level.id} space="xs">
                  <HStack justifyContent="space-between">
                    <Text size="sm">{level.label}</Text>
                    <Text size="sm">
                      {count}
                      {' '}
                      tasks (
                      {percentage}
                      %)
                    </Text>
                  </HStack>
                  <Box w="100%" h="$1" bg="$backgroundLight200">
                    <Box w={`${percentage}%`} h="100%" bg={dimensionColor} opacity={level.id === 'n/a' ? 0.3 : 1} />
                  </Box>
                </VStack>
              );
            })}
          </VStack>
        </Box>
      </Box>
    </Modal>
  );
};

const DimensionInsightCard: React.FC<{
  dimension: string;
  stats: DimensionStats;
  dimensionColor: string;
}> = ({
  dimension, stats, dimensionColor,
}) => {
  const [isDetailVisible, setIsDetailVisible] = useState(false);
  const { t } = useTranslation();
  const { performanceLevels } = useEvaluationGradingConfig();

  return (
    <>
      <Pressable onPress={() => setIsDetailVisible(true)}>
        <Card
          borderLeftWidth={3}
          borderLeftColor={dimensionColor}
          bg="white"
          borderRadius={0}
          borderTopRightRadius={6}
          borderBottomRightRadius={6}
          p={0}
          height={140}
        >
          <HStack flex={1} p="$4" alignItems="flex-start" space="lg">
            <VStack flex={1} space="xs" h="100%" justifyContent="space-between">
              <Box height={18}>
                <HStack alignItems="center" space="sm">
                  <Text size="xs" color={dimensionColor} textTransform="uppercase" fontWeight="bold">
                    {t(`common.objective-dimensions.${dimension}`)}
                  </Text>
                </HStack>
              </Box>

              <Box w="100%" h="$1" bg="$backgroundLight200" my="$2">
                <Box w={`${Math.round(stats.score * 100)}%`} h="100%" bg={dimensionColor} />
              </Box>

              <Box height={14}>
                <Text size="xs" color="$textSubtle" numberOfLines={1}>
                  {stats.total === 0
                    ? 'No tasks evaluated'
                    : (formatPerformanceLevelDisplay(stats.score, performanceLevels) || '—')}
                </Text>
              </Box>

              <Box height={30} justifyContent="flex-start">
                <Text size="2xs" color="$textSubtle" numberOfLines={2} ellipsizeMode="tail">
                  {t(`student.grading.dimensionHints.${dimension}`)}
                </Text>
              </Box>
            </VStack>
          </HStack>
        </Card>
      </Pressable>

      <DimensionDetailModal
        dimension={dimension}
        stats={stats}
        isVisible={isDetailVisible}
        onClose={() => setIsDetailVisible(false)}
        performanceLevels={performanceLevels}
        dimensionColor={dimensionColor}
      />
    </>
  );
};

const EssayDimensionInsights: React.FC<EssayDimensionInsightsProps> = ({ tasks }) => {
  const {
    evalDimensionKeys,
    performanceLevels,
    performanceLevelIds,
    dimensionColors,
    levelWeights,
  } = useEvaluationGradingConfig();
  const { eventKinds } = useTenantEventKinds();
  const { t } = useTranslation();
  // Find the n/a level ID
  const naLevelId = useMemo(() => {
    const naLevel = performanceLevels.find((level) => level.id === 'n/a' || level.label === 'n/a');
    return naLevel?.id || 'n/a';
  }, [performanceLevels]);

  const dimensionStats = useMemo(() => {
    const stats: Record<string, DimensionStats> = {};

    // Initialize stats for each dimension
    evalDimensionKeys.forEach((dim) => {
      stats[dim] = {
        total: 0,
        counts: performanceLevelIds.reduce((acc, levelId) => {
          acc[levelId] = 0;
          return acc;
        }, {} as Record<string, number>),
        predominantLevel: naLevelId,
        applicableCount: 0,
        comments: [],
        score: 0,
        transferTaskCount: 0,
        regularTaskCount: 0,
      };
    });

    // First pass: Count tasks and collect evaluations
    tasks.forEach((task) => {
      if (task?.type === 'oral_exam') return;
      if (!taskRequiresEvaluation(task, eventKinds)) return;
      if (!task.submission?.evaluation) return;

      const dimEvals = getFlatEvaluationDimensions(task.submission.evaluation);
      evalDimensionKeys.forEach((dimension) => {
        const evaluation: EvalDimRating | undefined = dimEvals[dimension];
        if (!evaluation || !evaluation.level) {
          stats[dimension].counts[naLevelId] += 1;
          return;
        }

        // Count by task type
        if (task.gradingSystem === 'full_evaluation') {
          stats[dimension].transferTaskCount += 1;
        } else {
          stats[dimension].regularTaskCount += 1;
        }

        stats[dimension].total += 1;
        stats[dimension].counts[evaluation.level] += 1;
        stats[dimension].applicableCount += 1;

        if (evaluation.comment) {
          stats[dimension].comments.push(evaluation.comment);
        }
      });
    });

    // Second pass: Calculate weighted scores and predominant levels
    Object.keys(stats).forEach((dimension) => {
      const dimStats = stats[dimension];

      // Calculate separate scores for transfer and regular tasks
      let transferScore = 0;
      let regularScore = 0;
      let transferCount = 0;
      let regularCount = 0;

      tasks.forEach((task) => {
        if (task?.type === 'oral_exam') return;
        if (!taskRequiresEvaluation(task, eventKinds)) return;
        const evaluation: EvalDimRating | undefined = getFlatEvaluationDimensions(task.submission?.evaluation)[dimension];
        if (!evaluation || !evaluation.level || evaluation.level === naLevelId) return;

        const weight = levelWeights[evaluation.level] || 0;

        if (task.gradingSystem === 'full_evaluation') {
          transferScore += weight;
          transferCount += 1;
        } else {
          regularScore += weight;
          regularCount += 1;
        }
      });

      // Transfer tasks count for 80%, regular tasks for 20% — when both are present.
      // Normalize by the sum of applicable weights so a module with only regular tasks
      // (or only transfer tasks) still scores 0–100% rather than being capped at 20%/80%.
      const transferAvg = transferCount > 0 ? transferScore / transferCount : 0;
      const regularAvg = regularCount > 0 ? regularScore / regularCount : 0;
      const transferWeight = transferCount > 0 ? 0.8 : 0;
      const regularWeight = regularCount > 0 ? 0.2 : 0;
      const totalWeight = transferWeight + regularWeight;

      dimStats.score = totalWeight > 0
        ? (transferAvg * transferWeight + regularAvg * regularWeight) / totalWeight
        : 0;

      // Find predominant level (excluding n/a)
      let maxCount = 0;
      let predominant = naLevelId;

      performanceLevelIds.forEach((levelId) => {
        if (levelId !== naLevelId && dimStats.counts[levelId] > maxCount) {
          maxCount = dimStats.counts[levelId];
          predominant = levelId;
        }
      });

      if (maxCount > 0) {
        dimStats.predominantLevel = predominant;
      }
    });

    return stats;
  }, [tasks, evalDimensionKeys, performanceLevelIds, naLevelId, levelWeights, eventKinds]);

  // Create rows of dimensions (2 per row)
  const dimensionRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < evalDimensionKeys.length; i += 2) {
      rows.push(evalDimensionKeys.slice(i, i + 2));
    }
    return rows;
  }, [evalDimensionKeys]);

  return (
    <Box p="$2">
      <VStack space="lg">
        <VStack space="xs">
          <Heading size="md">Evaluation Overview</Heading>
          <Text size="sm" color="$textSubtle">
            {t('student.grading.evaluationOverview')}
          </Text>
        </VStack>

        {/* Dimensions in rows of 2 */}
        <VStack space="md">
          {dimensionRows.map((row) => (
            <HStack key={`dimension-row-${row.join('-')}`} space="md" alignItems="stretch">
              {row.map((dim) => (
                <Box key={dim} flex={1}>
                  <DimensionInsightCard
                    dimension={dim}
                    stats={dimensionStats[dim]}
                    dimensionColor={dimensionColors[dim] || '#666666'}
                  />
                </Box>
              ))}
              {/* If odd number of dimensions, add empty box to maintain grid */}
              {row.length === 1 && <Box flex={1} />}
            </HStack>
          ))}
        </VStack>
      </VStack>
    </Box>
  );
};

export default EssayDimensionInsights;
